import { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadOrderDocument, openOrderDocument, type OrderDocument } from "@/lib/api";

interface Props {
  orderId: number;
  /** `compact` shows icon-only download buttons for a list row. */
  compact?: boolean;
  onError?: (message: string) => void;
}

/** Receipt and invoice buttons for one paid order. */
export default function DocumentButtons({ orderId, compact = false, onError }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    try {
      await action();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : t("orders.documents.failed"));
    } finally {
      setBusy(null);
    }
  };

  const kinds: { kind: OrderDocument; icon: string }[] = [
    { kind: "receipt", icon: "ri-receipt-line" },
    { kind: "invoice", icon: "ri-file-list-3-line" },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        {kinds.map(({ kind, icon }) => (
          <button
            key={kind}
            type="button"
            title={t(`orders.documents.download_${kind}`)}
            aria-label={t(`orders.documents.download_${kind}`)}
            disabled={busy !== null}
            onClick={() => run(`${kind}:download`, () => downloadOrderDocument(orderId, kind))}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-200/70 hover:text-primary-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            <i className={busy === `${kind}:download` ? "ri-loader-4-line animate-spin" : icon}></i>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {kinds.map(({ kind, icon }) => (
        <div
          key={kind}
          className="flex items-center justify-between gap-3 rounded-md border border-background-300/60 bg-background-50 px-4 py-3"
        >
          <div className="flex items-center gap-2 min-w-0">
            <i className={`${icon} text-lg text-primary-600`}></i>
            <span className="text-sm font-semibold text-foreground-900 truncate">
              {t(`orders.documents.${kind}`)}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => run(`${kind}:view`, () => openOrderDocument(orderId, kind))}
              className="whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:bg-background-200/70 transition-colors disabled:opacity-50 cursor-pointer"
            >
              {busy === `${kind}:view` ? <i className="ri-loader-4-line animate-spin"></i> : t("orders.documents.view")}
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => run(`${kind}:download`, () => downloadOrderDocument(orderId, kind))}
              className="whitespace-nowrap inline-flex items-center gap-1 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer"
            >
              <i className={busy === `${kind}:download` ? "ri-loader-4-line animate-spin" : "ri-download-2-line"}></i>
              {t("common.actions.download")}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
